import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SchoolIcon from '@mui/icons-material/School';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// 科技蓝青配色方案
const COLORS = {
  primary: '#3b82f6',
  secondary: '#06b6d4',
  accent: '#10b981',
  text: '#f1f5f9',
  textMuted: '#94a3b8',
  bgDark: '#0a0f1a',
  bgCard: 'rgba(15, 23, 42, 0.95)',
  border: 'rgba(59, 130, 246, 0.25)',
  glow: 'rgba(59, 130, 246, 0.4)',
  wechatGreen: '#07c160',
  warning: '#f59e0b',
};

// 公众号配置
const WECHAT_CONFIG = {
  name: 'KM智能写作',
  qrCode: '/wechat-qrcode.png',
  description: '扫码关注获取完整使用教程',
  benefits: [
    { icon: 'book', text: '新手入门教程' },
    { icon: 'star', text: '番茄小说发布指南' },
    { icon: 'tips', text: '提升收益的技巧' },
    { icon: 'update', text: '最新功能使用说明' }
  ]
};

// 检测是否为移动设备
const isMobileDevice = () => {
  // 检查 User Agent
  const ua = navigator.userAgent.toLowerCase();
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // 检查触摸点数量
  const hasTouch = navigator.maxTouchPoints && navigator.maxTouchPoints > 0;

  // 检查屏幕宽度（768px 以下视为移动端）
  const isSmallScreen = typeof window !== 'undefined' && window.innerWidth <= 768;

  // 检查是否为移动端
  return isMobileUA || (hasTouch && isSmallScreen);
};

// 复制文本到剪贴板
const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // 降级方案
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    }
  } catch (err) {
    console.error('复制失败:', err);
    return false;
  }
};

// 跳转到微信（移动端）
const jumpToWechat = () => {
  // 微信搜索页面
  window.location.href = 'weixin://';
};

// 可靠保存二维码图片（兼容 PC 和手机浏览器）
const saveQRCodeImage = async (imageUrl, fileName = '公众号二维码.png') => {
  try {
    // 方法1：使用 fetch + Blob（推荐，兼容性最好）
    const response = await fetch(imageUrl);
    if (!response.ok) throw new Error('图片加载失败');

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    // 创建下载链接
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);

    // 触发下载
    link.click();

    // 清理
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    }, 100);

    return true;
  } catch (err) {
    console.error('保存失败:', err);

    // 方法2：降级方案 - 使用 canvas
    try {
      return await saveImageWithCanvas(imageUrl, fileName);
    } catch (e) {
      console.error('Canvas 方案也失败:', e);
      return false;
    }
  }
};

// 使用 Canvas 保存图片（降级方案）
const saveImageWithCanvas = (imageUrl, fileName) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // 允许跨域

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Canvas 转 Blob 失败'));
            return;
          }

          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();

          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
            resolve(true);
          }, 100);
        }, 'image/png');
      } catch (e) {
        reject(e);
      }
    };

    img.onerror = () => reject(new Error('图片加载失败'));
    img.src = imageUrl;
  });
};

// 登录页面用的小型引导卡片
export const WechatGuideCard = ({ compact = false }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // 初始检测
    setIsMobile(isMobileDevice());

    // 监听窗口大小变化
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCopy = async () => {
    const success = await copyToClipboard(WECHAT_CONFIG.name);
    if (success) {
      setCopied(true);
      setSnackbar({ open: true, message: '已复制公众号名称，请在微信内搜索', type: 'success' });
      setTimeout(() => setCopied(false), 2000);
    } else {
      setSnackbar({ open: true, message: '复制失败，请手动长按复制', type: 'error' });
    }
  };

  // 点击卡片（PC端和手机端都打开弹窗）
  const handleCardClick = () => {
    setDialogOpen(true);
  };

  // 弹窗中复制公众号名称
  const handleCopyAndJump = async () => {
    const success = await copyToClipboard(WECHAT_CONFIG.name);
    if (success) {
      setCopied(true);
      setSnackbar({ open: true, message: '已复制公众号名称，正在跳转微信...', type: 'success' });
      // 延迟跳转微信
      setTimeout(() => {
        jumpToWechat();
      }, 500);
    } else {
      setSnackbar({ open: true, message: '复制失败，请手动长按复制', type: 'error' });
    }
  };

  if (!compact) return null;

  return (
    <>
      <Box
        onClick={handleCardClick}
        sx={{
          mt: 2,
          p: 2,
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(7, 193, 96, 0.1) 100%)',
          border: '2px solid rgba(245, 158, 11, 0.4)',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          animation: 'pulse 2s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.4)' },
            '50%': { boxShadow: '0 0 20px 5px rgba(245, 158, 11, 0.2)' },
          },
          '&:hover': {
            borderColor: COLORS.warning,
            transform: 'translateY(-2px)',
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* 教程图标 */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
            }}
          >
            <MenuBookIcon sx={{ color: '#fff', fontSize: 26 }} />
          </Box>

          {/* 文字信息 */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ color: '#fff', fontSize: '0.95rem', fontWeight: 700 }}>
                新手必看使用教程
              </Typography>
              <Box
                sx={{
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  px: 0.8,
                  py: 0.2,
                  borderRadius: '4px',
                  animation: 'blink 1s ease-in-out infinite',
                  '@keyframes blink': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                  },
                }}
              >
                重要
              </Box>
            </Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', mt: 0.3 }}>
              关注公众号，获取完整使用教程与技巧
            </Typography>
          </Box>

          {/* 箭头或图标 */}
          <Box sx={{ color: COLORS.warning, display: 'flex', alignItems: 'center' }}>
            {isMobile ? (
              <CheckIcon sx={{ fontSize: 22 }} />
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            )}
          </Box>
        </Box>
      </Box>

      {/* 详情弹窗 */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: COLORS.bgCard,
            backdropFilter: 'blur(32px)',
            border: '1px solid ' + COLORS.border,
            borderRadius: '20px',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(7, 193, 96, 0.15) 100%)',
            borderBottom: '1px solid ' + COLORS.border,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SchoolIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 700 }}>
                使用教程
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>
                新手用户必看
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setDialogOpen(false)} sx={{ color: COLORS.textMuted }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 3,
              alignItems: 'center',
            }}
          >
            {/* 二维码 */}
            <Box
              sx={{
                flexShrink: 0,
                p: 2,
                background: '#fff',
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(245, 158, 11, 0.25)',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  px: 1.5,
                  py: 0.3,
                  borderRadius: '4px',
                }}
              >
                扫码关注
              </Box>
              <img
                src={WECHAT_CONFIG.qrCode}
                alt="公众号二维码"
                style={{ width: 160, height: 160, display: 'block', marginTop: 5 }}
              />
              {/* 保存按钮 - 手机端显示 */}
              {isMobile && (
                <Button
                  size="small"
                  onClick={async () => {
                    const success = await saveQRCodeImage(WECHAT_CONFIG.qrCode, 'KM智能写作-公众号二维码.png');
                    if (success) {
                      setSnackbar({ open: true, message: '二维码已保存到相册', type: 'success' });
                    } else {
                      setSnackbar({ open: true, message: '保存失败，请长按图片保存', type: 'error' });
                    }
                  }}
                  sx={{
                    mt: 1.5,
                    width: '100%',
                    background: 'rgba(7, 193, 96, 0.1)',
                    color: COLORS.wechatGreen,
                    border: '1px solid rgba(7, 193, 96, 0.3)',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'rgba(7, 193, 96, 0.2)',
                    }
                  }}
                  startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
                >
                  保存二维码
                </Button>
              )}
            </Box>

            {/* 信息 */}
            <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography
                sx={{
                  color: '#fff',
                  fontSize: '1.3rem',
                  fontWeight: 700,
                  mb: 1,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #07c160 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {WECHAT_CONFIG.name}
              </Typography>

              <Typography sx={{ color: COLORS.textMuted, fontSize: '0.9rem', mb: 2 }}>
                {WECHAT_CONFIG.description}
              </Typography>

              {/* 教程列表 */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
                  关注后获取：
                </Typography>
                {WECHAT_CONFIG.benefits.map((benefit, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 0.8,
                      color: 'rgba(255,255,255,0.8)',
                      fontSize: '0.85rem',
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: '#f59e0b',
                      }}
                    />
                    {benefit.text}
                  </Box>
                ))}
              </Box>

              {/* 复制公众号名称 */}
              <Box
                onClick={handleCopy}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1,
                  background: 'rgba(7, 193, 96, 0.1)',
                  border: '1px solid rgba(7, 193, 96, 0.3)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'rgba(7, 193, 96, 0.15)',
                    borderColor: COLORS.wechatGreen,
                  }
                }}
              >
                <Typography sx={{ color: '#fff', fontSize: '0.85rem' }}>
                  {WECHAT_CONFIG.name}
                </Typography>
                {copied ? (
                  <CheckIcon sx={{ color: COLORS.wechatGreen, fontSize: 16 }} />
                ) : (
                  <ContentCopyIcon sx={{ color: COLORS.textMuted, fontSize: 16 }} />
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          {isMobile ? (
            // 手机端：复制并跳转
            <Button
              variant="contained"
              fullWidth
              onClick={handleCopyAndJump}
              sx={{
                background: 'linear-gradient(135deg, #07c160 0%, #059669 100%)',
                color: '#fff',
                fontWeight: 700,
                py: 1.2,
                borderRadius: '10px',
                boxShadow: '0 4px 16px rgba(7, 193, 96, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                }
              }}
              startIcon={<CheckIcon />}
            >
              复制公众号名称并跳转微信
            </Button>
          ) : (
            // PC端：确认关闭
            <>
              <Button
                onClick={() => setDialogOpen(false)}
                sx={{
                  color: COLORS.textMuted,
                  '&:hover': { background: 'rgba(255, 255, 255, 0.05)' }
                }}
              >
                稍后再说
              </Button>
              <Button
                variant="contained"
                onClick={() => setDialogOpen(false)}
                sx={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: '#fff',
                  fontWeight: 700,
                  px: 3,
                  borderRadius: '8px',
                  boxShadow: '0 4px 16px rgba(245, 158, 11, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                  }
                }}
              >
                我已知晓
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* 提示消息 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type}
          sx={{
            background: snackbar.type === 'success'
              ? 'linear-gradient(135deg, #07c160 0%, #059669 100%)'
              : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: '#fff',
            fontWeight: 600,
            borderRadius: '10px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

// 主界面用的顶部横幅
export const WechatGuideBanner = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem('wechatGuideDismissed') === 'true';
  });
  const [copied, setCopied] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // 初始检测
    setIsMobile(isMobileDevice());

    // 监听窗口大小变化
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    localStorage.setItem('wechatGuideDismissed', 'true');
  };

  const handleCopy = async () => {
    const success = await copyToClipboard(WECHAT_CONFIG.name);
    if (success) {
      setCopied(true);
      setSnackbar({ open: true, message: '已复制公众号名称，请在微信内搜索', type: 'success' });
      setTimeout(() => setCopied(false), 2000);
    } else {
      setSnackbar({ open: true, message: '复制失败，请手动长按复制', type: 'error' });
    }
  };

  // 弹窗中复制公众号名称并跳转
  const handleCopyAndJump = async () => {
    const success = await copyToClipboard(WECHAT_CONFIG.name);
    if (success) {
      setCopied(true);
      setSnackbar({ open: true, message: '已复制公众号名称，正在跳转微信...', type: 'success' });
      // 延迟跳转微信
      setTimeout(() => {
        jumpToWechat();
      }, 500);
    } else {
      setSnackbar({ open: true, message: '复制失败，请手动长按复制', type: 'error' });
    }
  };

  // 点击卡片（PC端和手机端都打开弹窗）
  const handleCardClick = () => {
    setDialogOpen(true);
  };

  if (dismissed && !visible) return null;

  return (
    <>
      <Box
        sx={{
          mb: 2,
          p: 2,
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(7, 193, 96, 0.08) 100%)',
          border: '2px solid rgba(245, 158, 11, 0.35)',
          borderRadius: '14px',
          position: 'relative',
          overflow: 'hidden',
          animation: 'pulse 2s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.4)' },
            '50%': { boxShadow: '0 0 25px 8px rgba(245, 158, 11, 0.15)' },
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, #f59e0b, #07c160, #f59e0b)',
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          {/* 左侧内容 */}
          <Box
            onClick={handleCardClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              flex: 1,
              minWidth: 0,
            }}
          >
            {/* 教程图标 */}
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 4px 16px rgba(245, 158, 11, 0.35)',
                animation: 'bounce 2s ease-in-out infinite',
                '@keyframes bounce': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-3px)' },
                },
              }}
            >
              <MenuBookIcon sx={{ color: '#fff', fontSize: 26 }} />
            </Box>

            {/* 文字信息 */}
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ color: '#fff', fontSize: '1rem', fontWeight: 700 }}>
                  新手必看使用教程
                </Typography>
                <Box
                  sx={{
                    background: '#ef4444',
                    color: '#fff',
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    px: 0.8,
                    py: 0.2,
                    borderRadius: '4px',
                    animation: 'blink 1s ease-in-out infinite',
                    '@keyframes blink': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                    },
                  }}
                >
                  重要
                </Box>
              </Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', mt: 0.3 }}>
                关注公众号，获取完整使用教程与写作技巧
              </Typography>
            </Box>
          </Box>

          {/* 右侧按钮 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              onClick={handleCardClick}
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: '#fff',
                fontSize: '0.85rem',
                fontWeight: 700,
                px: 2.5,
                py: 0.8,
                borderRadius: '10px',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                }
              }}
              startIcon={<QrCodeIcon sx={{ fontSize: 18 }} />}
            >
              查看教程
            </Button>
            <IconButton
              onClick={handleDismiss}
              size="small"
              sx={{
                color: COLORS.textMuted,
                '&:hover': { background: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {/* 教程内容提示 - 仅PC端显示 */}
        {!isMobile && (
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              gap: 2,
              mt: 2,
              pt: 2,
              borderTop: '1px solid rgba(245, 158, 11, 0.15)',
            }}
          >
            {WECHAT_CONFIG.benefits.map((benefit, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.8,
                  color: 'rgba(255,255,255,0.75)',
                  fontSize: '0.8rem',
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: '#f59e0b',
                  }}
                />
                {benefit.text}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* 详情弹窗 */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: COLORS.bgCard,
            backdropFilter: 'blur(32px)',
            border: '1px solid ' + COLORS.border,
            borderRadius: '20px',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(7, 193, 96, 0.15) 100%)',
            borderBottom: '1px solid ' + COLORS.border,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SchoolIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 700 }}>
                使用教程
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>
                新手用户必看
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setDialogOpen(false)} sx={{ color: COLORS.textMuted }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 3,
              alignItems: 'center',
            }}
          >
            {/* 二维码 */}
            <Box
              sx={{
                flexShrink: 0,
                p: 2,
                background: '#fff',
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(245, 158, 11, 0.25)',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  px: 1.5,
                  py: 0.3,
                  borderRadius: '4px',
                }}
              >
                扫码关注
              </Box>
              <img
                src={WECHAT_CONFIG.qrCode}
                alt="公众号二维码"
                style={{ width: 160, height: 160, display: 'block', marginTop: 5 }}
              />
              {/* 保存按钮 - 手机端显示 */}
              {isMobile && (
                <Button
                  size="small"
                  onClick={async () => {
                    const success = await saveQRCodeImage(WECHAT_CONFIG.qrCode, 'KM智能写作-公众号二维码.png');
                    if (success) {
                      setSnackbar({ open: true, message: '二维码已保存到相册', type: 'success' });
                    } else {
                      setSnackbar({ open: true, message: '保存失败，请长按图片保存', type: 'error' });
                    }
                  }}
                  sx={{
                    mt: 1.5,
                    width: '100%',
                    background: 'rgba(7, 193, 96, 0.1)',
                    color: COLORS.wechatGreen,
                    border: '1px solid rgba(7, 193, 96, 0.3)',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'rgba(7, 193, 96, 0.2)',
                    }
                  }}
                  startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
                >
                  保存二维码
                </Button>
              )}
            </Box>

            {/* 信息 */}
            <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography
                sx={{
                  color: '#fff',
                  fontSize: '1.3rem',
                  fontWeight: 700,
                  mb: 1,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #07c160 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {WECHAT_CONFIG.name}
              </Typography>

              <Typography sx={{ color: COLORS.textMuted, fontSize: '0.9rem', mb: 2 }}>
                {WECHAT_CONFIG.description}
              </Typography>

              {/* 教程列表 */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
                  关注后获取：
                </Typography>
                {WECHAT_CONFIG.benefits.map((benefit, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 0.8,
                      color: 'rgba(255,255,255,0.8)',
                      fontSize: '0.85rem',
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: '#f59e0b',
                      }}
                    />
                    {benefit.text}
                  </Box>
                ))}
              </Box>

              {/* 复制公众号名称 */}
              <Box
                onClick={handleCopy}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1,
                  background: 'rgba(7, 193, 96, 0.1)',
                  border: '1px solid rgba(7, 193, 96, 0.3)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'rgba(7, 193, 96, 0.15)',
                    borderColor: COLORS.wechatGreen,
                  }
                }}
              >
                <Typography sx={{ color: '#fff', fontSize: '0.85rem' }}>
                  {WECHAT_CONFIG.name}
                </Typography>
                {copied ? (
                  <CheckIcon sx={{ color: COLORS.wechatGreen, fontSize: 16 }} />
                ) : (
                  <ContentCopyIcon sx={{ color: COLORS.textMuted, fontSize: 16 }} />
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          {isMobile ? (
            // 手机端：复制并跳转
            <Button
              variant="contained"
              fullWidth
              onClick={handleCopyAndJump}
              sx={{
                background: 'linear-gradient(135deg, #07c160 0%, #059669 100%)',
                color: '#fff',
                fontWeight: 700,
                py: 1.2,
                borderRadius: '10px',
                boxShadow: '0 4px 16px rgba(7, 193, 96, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                }
              }}
              startIcon={<CheckIcon />}
            >
              复制公众号名称并跳转微信
            </Button>
          ) : (
            // PC端：确认关闭
            <>
              <Button
                onClick={() => setDialogOpen(false)}
                sx={{
                  color: COLORS.textMuted,
                  '&:hover': { background: 'rgba(255, 255, 255, 0.05)' }
                }}
              >
                稍后再说
              </Button>
              <Button
                variant="contained"
                onClick={() => setDialogOpen(false)}
                sx={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: '#fff',
                  fontWeight: 700,
                  px: 3,
                  borderRadius: '8px',
                  boxShadow: '0 4px 16px rgba(245, 158, 11, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                  }
                }}
              >
                我已知晓
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* 提示消息 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type}
          sx={{
            background: snackbar.type === 'success'
              ? 'linear-gradient(135deg, #07c160 0%, #059669 100%)'
              : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: '#fff',
            fontWeight: 600,
            borderRadius: '10px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default { WechatGuideCard, WechatGuideBanner };
